import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Calendar, X } from "lucide-react";

const AccomplishmentsModal = ({
  title,
  company,
  startDate,
  endDate,
  accomplishments,
  onClose,
}) => {
  useEffect(() => {
    const onKeyDown = (e) => e.key === "Escape" && onClose();
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = overflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby="accomplishments-title"
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
        className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3
              id="accomplishments-title"
              className="font-display font-semibold text-xl text-secondary-900"
            >
              {title}
            </h3>
            <p className="text-secondary-600 font-medium">{company}</p>
            <div className="flex items-center space-x-1 mt-1 text-sm text-secondary-500">
              <Calendar className="w-4 h-4" />
              <span>
                {startDate} - {endDate}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-2 rounded-lg text-secondary-500 hover:bg-secondary-100 hover:text-secondary-900 transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <ul className="space-y-3">
          {accomplishments.map((accomplishment, index) => (
            <li key={index} className="flex items-start space-x-2">
              <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
              <span className="text-secondary-700 text-sm leading-relaxed">
                {accomplishment}
              </span>
            </li>
          ))}
        </ul>
      </motion.div>
    </motion.div>,
    document.body,
  );
};

const Card = ({
  title,
  company,
  icon,
  startDate,
  endDate,
  accomplishments,
  isEven,
}) => {
  const [showAll, setShowAll] = useState(false);
  const closeModal = useCallback(() => setShowAll(false), []);

  const cardVariants = {
    hidden: { opacity: 0, x: isEven ? -50 : 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <motion.div
      variants={cardVariants}
      whileHover={{ scale: 1.02 }}
      className={`bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border-l-4 ${
        isEven ? "border-primary-500" : "border-accent-500"
      }`}
    >
      <div className="flex items-start space-x-4">
        <div className="w-16 h-16 bg-secondary-100 rounded-lg flex items-center justify-center flex-shrink-0">
          {icon && (
            <img
              src={icon}
              alt={`${company} logo`}
              className="w-12 h-12 object-contain"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
          )}
          <div
            className={`w-12 h-12 bg-secondary-200 rounded items-center justify-center ${
              icon ? "hidden" : "flex"
            }`}
          >
            <span className="text-secondary-600 font-bold text-lg">
              {company.charAt(0)}
            </span>
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="font-display font-semibold text-lg text-secondary-900 mb-1">
            {title}
          </h3>
          <p className="text-secondary-600 font-medium mb-2">{company}</p>

          <div className="flex items-center space-x-4 mb-4 text-sm text-secondary-500">
            <div className="flex items-center space-x-1">
              <Calendar className="w-4 h-4" />
              <span>
                {startDate} - {endDate}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            {accomplishments.slice(0, 2).map((accomplishment, index) => (
              <p
                key={index}
                className="text-secondary-600 text-sm leading-relaxed"
              >
                • {accomplishment}
              </p>
            ))}
            {accomplishments.length > 2 && (
              <button
                onClick={() => setShowAll(true)}
                className="text-primary-600 hover:text-primary-700 hover:underline text-sm font-medium"
              >
                +{accomplishments.length - 2} more achievements
              </button>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showAll && (
          <AccomplishmentsModal
            title={title}
            company={company}
            startDate={startDate}
            endDate={endDate}
            accomplishments={accomplishments}
            onClose={closeModal}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Card;
